/*
 * Portal-app-side mirror of the tenant-settings MFE's ColorPaletteCatalog
 * (Clients/mfe/packages/tenant-settings/src/utils/color-palette-catalog.ts).
 *
 * Duplicated rather than shared because Clients/Web and Clients/mfe are separate npm
 * workspaces/deployables with no build-time dependency between them. Both copies must stay in
 * sync when a family is added/changed.
 */

export interface ColorPaletteFamilyShades {
    primary: string;
    secondary: string;
    lightest: string;
}

export interface ColorPaletteFamily extends ColorPaletteFamilyShades {
    key: string;
    displayName: string;
}

export const COLOR_PALETTE_FAMILIES: ColorPaletteFamily[] = [
    /*
     * Named palette catalog (CPO-4336 design reference) — hex values best-effort derived from
     * well-known standard color-name libraries (no matching design-token source found in this
     * repo or @servicetitan/anvil2 for these specific names). `primary` is the reference color;
     * `secondary`/`lightest` are generated by lightening `primary` in HSL space to match this
     * catalog's existing shade-ramp pattern. Must stay in sync with the tenant-settings MFE's
     * copy of this catalog (see file-header note). Flag for design review before merge.
     */
    {
        key: 'river-bed',
        displayName: 'River Bed',
        primary: '#4E5D6C',
        secondary: '#91ADCA',
        lightest: '#EBF0F4',
    },
    {
        key: 'science-blue',
        displayName: 'Science Blue',
        primary: '#0176D3',
        secondary: '#5DB7FE',
        lightest: '#E1F1FF',
    },
    {
        key: 'tomato',
        displayName: 'Tomato',
        primary: '#E4634C',
        secondary: '#EA8371',
        lightest: '#FBE8E4',
    },
    {
        key: 'salem',
        displayName: 'Salem',
        primary: '#0A7B3E',
        secondary: '#68F3A8',
        lightest: '#E3FDEF',
    },
    {
        key: 'notorius',
        displayName: 'Notorius',
        primary: '#96343A',
        secondary: '#D5868B',
        lightest: '#F7E8E9',
    },
    {
        key: 'pacific-blue',
        displayName: 'Pacific Blue',
        primary: '#00A5C6',
        secondary: '#5CE4FF',
        lightest: '#E0FAFF',
    },
    {
        key: 'plum',
        displayName: 'Plum',
        primary: '#7D3C7D',
        secondary: '#CA91CA',
        lightest: '#F5EAF5',
    },
    {
        key: 'grigio',
        displayName: 'Grigio',
        primary: '#8B8987',
        secondary: '#CAAD91',
        lightest: '#F4F0EB',
    },
    {
        key: 'alberta',
        displayName: 'Alberta',
        primary: '#3F91D9',
        secondary: '#77B1E4',
        lightest: '#E5F0FA',
    },
    {
        key: 'bay-of-many',
        displayName: 'Bay of Many',
        primary: '#2B4A87',
        secondary: '#83A0D8',
        lightest: '#E8EDF8',
    },
    {
        key: 'astronaut-blue',
        displayName: 'Astronaut Blue',
        primary: '#003F5C',
        secondary: '#5CCCFF',
        lightest: '#E0F5FF',
    },
    {
        key: 'st-tropaz',
        displayName: 'St Tropaz',
        primary: '#2A5B8C',
        secondary: '#81ADD9',
        lightest: '#E7F0F8',
    },
    {
        key: 'east-bay',
        displayName: 'East Bay',
        primary: '#43536E',
        secondary: '#91A6CA',
        lightest: '#EBEFF4',
    },
    {
        key: 'bondi-blue',
        displayName: 'Bondi Blue',
        primary: '#0195B6',
        secondary: '#5DE1FE',
        lightest: '#E1F9FF',
    },
    {
        key: 'metallic-copper',
        displayName: 'Metallic Copper',
        primary: '#7C3A2D',
        secondary: '#D49487',
        lightest: '#F7EBE9',
    },
    {
        key: 'tall-poppy',
        displayName: 'Tall Poppy',
        primary: '#B02E2E',
        secondary: '#DD7E7E',
        lightest: '#F9E7E7',
    },
    {
        key: 'amaranth',
        displayName: 'Amaranth',
        primary: '#DC244D',
        secondary: '#E9728D',
        lightest: '#FBE5EA',
    },
    {
        key: 'fern-green',
        displayName: 'Fern Green',
        primary: '#4F7942',
        secondary: '#9ECA91',
        lightest: '#EDF4EB',
    },
    {
        key: 'himalaya',
        displayName: 'Himalaya',
        primary: '#6B6415',
        secondary: '#E4DB77',
        lightest: '#FAF8E5',
    },
    {
        key: 'fun-green',
        displayName: 'Fun Green',
        primary: '#008542',
        secondary: '#5CFFAD',
        lightest: '#E0FFF0',
    },
    {
        key: 'genoa',
        displayName: 'Genoa',
        primary: '#2B7A6F',
        secondary: '#86D4CA',
        lightest: '#E8F7F5',
    },
    {
        key: 'tango',
        displayName: 'Tango',
        primary: '#EE7326',
        secondary: '#F39D68',
        lightest: '#FDEDE3',
    },
    {
        key: 'carulean',
        displayName: 'Carulean',
        primary: '#0087BD',
        secondary: '#5CD0FF',
        lightest: '#E0F6FF',
    },
];

/**
 * Resolves a stored family key to its shades. Unset (null/undefined) or an unrecognized key
 * (e.g. a family removed in a later release) both resolve to `undefined` — the caller's signal
 * to render with no theme override, i.e. today's default blue (ARCH-SET-PALETTE-001 Failure Modes).
 */
export const getColorPaletteShadesByKey = (
    key: string | null | undefined
): ColorPaletteFamilyShades | undefined => {
    const family = COLOR_PALETTE_FAMILIES.find(family => family.key === key);
    return (
        family && {
            primary: family.primary,
            secondary: family.secondary,
            lightest: family.lightest,
        }
    );
};
